<template>
  <v-row>
    <!-- toolbar -->
    <v-col cols="12">
      <v-toolbar dense flat rounded color="primary" dark>
        <v-btn icon @click="$router.push('/auctioneerrecords')">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>

        <v-toolbar-title>
          {{ auctioneer.name || "Auctioneer Record" }}
        </v-toolbar-title>

        <v-spacer />

        <v-chip small outlined color="white" text-color="white">
          {{ isNewRecord ? "New Record" : "Legacy Record" }}
        </v-chip>

        <v-btn
          v-if="isNewRecord && canEditAuctioneers"
          small
          depressed
          color="white"
          class="primary--text ml-3"
          @click="editDialog = true"
        >
          <v-icon left small>mdi-pencil</v-icon>
          Edit
        </v-btn>

        <v-btn
          v-if="isNewRecord && canEditAuctioneers"
          small
          depressed
          color="error"
          class="ml-2"
          @click="deleteDialog = true"
        >
          <v-icon left small>mdi-delete-outline</v-icon>
          Delete
        </v-btn>
      </v-toolbar>
    </v-col>

    <!-- loading -->
    <v-col v-if="loading" cols="12">
      <v-card outlined>
        <v-card-text class="text-center py-10">
          <v-progress-circular indeterminate color="primary" />
        </v-card-text>
      </v-card>
    </v-col>

    <template v-else>
      <!-- summary -->
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="d-flex align-center">
            <div>
              <div class="text-h5 font-weight-bold">
                {{ auctioneer.name || "Unnamed Auctioneer" }}
              </div>

              <div class="text-caption grey--text">
                Record ID: {{ auctioneer.id }}
              </div>
            </div>

            <v-spacer />

            <v-chip
              v-if="isNewRecord"
              small
              outlined
              color="primary"
              class="mr-2"
            >
              {{ auctioneer.type === "company" ? "Company" : "Auctioneer" }}
            </v-chip>

            <v-chip
              v-if="isNewRecord"
              small
              :color="auctioneer.active ? 'success' : 'grey'"
              dark
            >
              {{ auctioneer.active ? "Active" : "Inactive" }}
            </v-chip>
          </v-card-title>

          <v-divider />

          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="6">
                <div class="text-caption text-uppercase grey--text">
                  Mailing Address
                </div>

                <div class="font-weight-medium">
                  {{ displayAddress }}
                </div>
              </v-col>

              <v-col v-if="isNewRecord" cols="12" md="3">
                <div class="text-caption text-uppercase grey--text">
                  Contact Email
                </div>

                <div class="font-weight-medium">
                  {{ auctioneer.contactEmail || "�" }}
                </div>
              </v-col>

              <v-col v-if="isNewRecord" cols="12" md="3">
                <div class="text-caption text-uppercase grey--text">
                  Phone Number
                </div>

                <div class="font-weight-medium">
                  {{ auctioneer.phoneNumber || "�" }}
                </div>
              </v-col>

              <v-col v-if="isNewRecord" cols="12" md="3">
                <div class="text-caption text-uppercase grey--text">
                  Default Commission
                </div>

                <div class="font-weight-medium">
                  {{ defaultCommissionDisplay }}
                </div>
              </v-col>

              <v-col v-if="!isNewRecord" cols="12" md="3">
                <div class="text-caption text-uppercase grey--text">
                  Total Sales YTD
                </div>

                <div class="text-h6 font-weight-bold">
                  {{ formatCurrency(auctioneer.salesYtd || 0) }}
                </div>
              </v-col>

              <v-col cols="12" md="3">
                <div class="text-caption text-uppercase grey--text">
                  Associated Records
                </div>

                <div class="text-h6 font-weight-bold">
                  {{ linkedAuctioneers.length }}
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- information -->
      <v-col cols="12" md="7">
        <v-card outlined>
          <v-card-title>
            {{ isNewRecord ? "Auctioneer Information" : "Record Information" }}
          </v-card-title>

          <v-divider />

          <v-card-text v-if="isNewRecord">
            <v-row dense>
              <v-col cols="12" sm="6">
                <div class="text-caption text-uppercase grey--text">
                  Commission Email
                </div>
                <div class="font-weight-medium">
                  {{ auctioneer.commissionEmail || "�" }}
                </div>
              </v-col>

              <v-col cols="12" sm="6">
                <div class="text-caption text-uppercase grey--text">
                  Sales Rep
                </div>
                <div class="font-weight-medium">
                  {{ auctioneer.salesRep || "�" }}
                </div>
              </v-col>

              <v-col cols="12" class="mt-4">
                <div class="text-caption text-uppercase grey--text mb-2">
                  Notes
                </div>

                <div class="auctioneer-info pa-4 grey lighten-4 rounded">
                  {{ auctioneer.notes || "No notes entered." }}
                </div>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-text v-else>
            <div
              class="auctioneer-info pa-4 grey lighten-4 rounded"
              v-html="auctioneer.info || '<p>No information entered.</p>'"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- associated records -->
      <v-col cols="12" md="5">
        <v-card outlined>
          <v-card-title>
            {{ associatedTitle }}
          </v-card-title>

          <v-divider />

          <v-card-text v-if="linkedAuctioneers.length">
            <v-list two-line class="pa-0">
              <div
                v-for="(item, index) in linkedAuctioneers"
                :key="item.id"
              >
                <v-list-item
                  class="px-0"
                  @click="openAuctioneer(item)"
                >
                  <v-list-item-avatar color="primary" class="white--text">
                    <span>{{ getInitials(item.name) }}</span>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      {{ item.name }}
                    </v-list-item-title>

                    <v-list-item-subtitle v-if="isNewRecord">
                      {{
                        item.type === "company"
                          ? "Auctioneer Company"
                          : "Auctioneer"
                      }}
                    </v-list-item-subtitle>

                    <v-list-item-subtitle v-else>
                      {{ item.address || "No mailing address" }}
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-icon color="primary">mdi-chevron-right</v-icon>
                  </v-list-item-action>
                </v-list-item>

                <v-divider
                  v-if="index < linkedAuctioneers.length - 1"
                />
              </div>
            </v-list>
          </v-card-text>

          <v-card-text v-else class="grey--text">
            No associated auctioneers or companies are assigned.
          </v-card-text>
        </v-card>
      </v-col>

      <!-- reporting -->
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="d-flex align-center">
            <span>
              {{
                isNewRecord && auctioneer.type === "company"
                  ? "Company Reporting"
                  : "Auctioneer Reporting"
              }}
            </span>

            <v-spacer />

            <v-chip small outlined color="primary">
              ER Name Matching
            </v-chip>
          </v-card-title>

          <v-divider />

          <v-card-text>
            <v-alert type="info" text class="mb-4">
              These Event Reports are matched against the existing
              <strong>auctioneerName</strong> and
              <strong>auctioneerCompany</strong> fields. Matching is
              case-insensitive and ignores extra spaces.
              <template v-if="isNewRecord && auctioneer.type === 'company'">
                Company records also include Event Reports that match any
                auctioneer currently linked to the company.
              </template>
              <template v-else-if="!isNewRecord">
                Legacy records are matched using the legacy record name only.
              </template>
              Duplicate Event Reports are counted only once.
            </v-alert>

            <v-row dense class="mb-4">
              <v-col cols="12" sm="6" md="3">
                <v-sheet outlined rounded class="pa-4">
                  <div class="text-caption text-uppercase grey--text">
                    Matched Event Reports
                  </div>
                  <div class="text-h6 font-weight-bold">
                    {{ filteredEventReports.length }}
                  </div>
                </v-sheet>
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <v-sheet outlined rounded class="pa-4">
                  <div class="text-caption text-uppercase grey--text">
                    Total Sales
                  </div>
                  <div class="text-h6 font-weight-bold">
                    {{ formatCurrency(reportTotalSales) }}
                  </div>
                </v-sheet>
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <v-sheet outlined rounded class="pa-4">
                  <div class="text-caption text-uppercase grey--text">
                    Packages Sold
                  </div>
                  <div class="text-h6 font-weight-bold">
                    {{ reportTotalPackages }}
                  </div>
                </v-sheet>
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <v-sheet outlined rounded class="pa-4">
                  <div class="text-caption text-uppercase grey--text">
                    Est. Commission
                  </div>

                  <div
                    v-if="isNewRecord && hasDefaultCommission"
                    class="text-h6 font-weight-bold"
                  >
                    {{ formatCurrency(reportEstimatedCommission) }}
                  </div>

                  <div
                    v-else
                    class="text-h6 font-weight-bold grey--text"
                  >
                    �
                  </div>

                  <div class="text-caption grey--text">
                    {{
                      isNewRecord
                        ? hasDefaultCommission
                          ? `Using ${defaultCommissionDisplay}`
                          : "No default commission set"
                        : "Legacy record has no default commission"
                    }}
                  </div>
                </v-sheet>
              </v-col>
            </v-row>

            <v-sheet outlined rounded class="pa-4 mb-4">
              <div class="d-flex align-center mb-3">
                <div>
                  <div class="text-subtitle-2 font-weight-bold">
                    Reporting Filters
                  </div>
                  <div class="text-caption grey--text">
                    Filter matched Event Reports by event date.
                  </div>
                </div>

                <v-spacer />

                <v-btn
                  small
                  text
                  color="primary"
                  :disabled="!hasActiveReportFilters"
                  @click="clearReportFilters"
                >
                  Clear Filters
                </v-btn>
              </div>

              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="reportYear"
                    :items="reportYearOptions"
                    label="Year"
                    clearable
                    outlined
                    dense
                    hide-details
                  />
                </v-col>

                <v-col cols="12" sm="4">
                  <v-select
                    v-model="reportQuarter"
                    :items="reportQuarterOptions"
                    item-text="text"
                    item-value="value"
                    label="Quarter"
                    clearable
                    outlined
                    dense
                    hide-details
                  />
                </v-col>

                <v-col cols="12" sm="4">
                  <v-select
                    v-model="reportMonth"
                    :items="reportMonthOptions"
                    item-text="text"
                    item-value="value"
                    label="Month"
                    clearable
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
              </v-row>
            </v-sheet>

            <v-text-field
              v-model="reportSearch"
              prepend-inner-icon="mdi-magnify"
              label="Search matched Event Reports"
              clearable
              outlined
              dense
              hide-details
              class="mb-4"
            />

            <v-data-table
              :headers="reportHeaders"
              :items="filteredEventReports"
              :search="reportSearch"
              :loading="eventReportsLoading"
              :items-per-page="25"
              class="elevation-0"
            >
              <template v-slot:item.eventReportNumber="{ item }">
                <strong>#{{ item.eventReportNumber }}</strong>
              </template>

              <template v-slot:item.eventDate="{ item }">
                {{ formatDate(item.eventDate) }}
              </template>

              <template v-slot:item.numberOfWinners="{ item }">
                {{ getPackageCount(item) }}
              </template>

              <template v-slot:item.eventReportTotal="{ item }">
                {{ formatCurrency(Number(item.eventReportTotal) || 0) }}
              </template>

              <template v-slot:item.estimatedCommission="{ item }">
                {{ formatCurrency(getEstimatedCommission(item)) }}
              </template>

              <template v-slot:item.matchValue="{ item }">
                <div>
                  <div v-if="item.auctioneerName">
                    <span class="grey--text text-caption">Name:</span>
                    {{ item.auctioneerName }}
                  </div>

                  <div v-if="item.auctioneerCompany">
                    <span class="grey--text text-caption">Company:</span>
                    {{ item.auctioneerCompany }}
                  </div>
                </div>
              </template>

              <template v-slot:no-data>
                <div class="py-8 grey--text">
                  {{
                    hasActiveReportFilters
                      ? "No matched Event Reports fall within the selected filters."
                      : "No existing Event Reports matched this record yet."
                  }}
                </div>
              </template>
            </v-data-table>
          </v-card-text>

        </v-card>
      </v-col>
    </template>

    <!-- edit dialog for new records -->
    <v-dialog
      v-if="isNewRecord"
      v-model="editDialog"
      max-width="900"
      persistent
    >
      <AuctioneerDetails
        :item="auctioneer"
        :companies="allAuctioneers"
        :loading="saving"
        mode="edit"
        @save="updateRecord"
        @cancel="editDialog = false"
      />
    </v-dialog>

    <!-- delete dialog -->
    <v-dialog
      v-if="isNewRecord"
      v-model="deleteDialog"
      max-width="600"
      persistent
    >
      <v-card :loading="deleting">
        <v-card-title class="text-h5">
          Delete {{ auctioneer.name }}?
        </v-card-title>

        <v-card-text class="text--primary">
          <p>
            This will permanently delete this
            {{ auctioneer.type === "company" ? "company" : "auctioneer" }}
            record.
          </p>

          <p v-if="auctioneer.type === 'company' && linkedAuctioneers.length">
            This company is currently associated with
            <strong>{{ linkedAuctioneers.length }}</strong>
            auctioneer{{ linkedAuctioneers.length === 1 ? "" : "s" }}.
            The company will also be removed from those auctioneer records.
          </p>

          <p class="error--text font-weight-bold mb-0">
            This action cannot be undone.
          </p>
        </v-card-text>

        <v-card-actions>
          <v-btn
            text
            color="primary"
            :disabled="deleting"
            @click="deleteDialog = false"
          >
            Cancel
          </v-btn>

          <v-spacer />

          <v-btn
            color="error"
            depressed
            :loading="deleting"
            :disabled="deleting"
            @click="deleteRecord"
          >
            Delete Record
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import portalsApp from "@/plugins/firebase";
import formatCurrency from "@/mixins/formatCurrency";
import { can } from "@/services/auth";
import AuctioneerDetails from "@/components/AuctioneerRecords/AuctioneerDetails.vue";

export default {
  name: "AuctioneerRecordDetail",

  components: {
    AuctioneerDetails,
  },

  mixins: [formatCurrency],

  data: () => ({
    loading: false,
    saving: false,
    deleting: false,
    eventReportsLoading: false,

    editDialog: false,
    deleteDialog: false,

    auctioneer: {},
    allAuctioneers: [],
    linkedAuctioneers: [],

    allEventReports: [],
    reportSearch: "",
    reportYear: null,
    reportQuarter: null,
    reportMonth: null,

    reportQuarterOptions: [
      { text: "Q1 (Jan - Mar)", value: 1 },
      { text: "Q2 (Apr - Jun)", value: 2 },
      { text: "Q3 (Jul - Sep)", value: 3 },
      { text: "Q4 (Oct - Dec)", value: 4 },
    ],

    reportMonthOptions: [
      { text: "January", value: 1 },
      { text: "February", value: 2 },
      { text: "March", value: 3 },
      { text: "April", value: 4 },
      { text: "May", value: 5 },
      { text: "June", value: 6 },
      { text: "July", value: 7 },
      { text: "August", value: 8 },
      { text: "September", value: 9 },
      { text: "October", value: 10 },
      { text: "November", value: 11 },
      { text: "December", value: 12 },
    ],

    reportHeaders: [
      { text: "Invoice Number", value: "eventReportNumber" },
      { text: "Event Date", value: "eventDate" },
      { text: "Event Name", value: "eventName" },
      { text: "Organization", value: "organizationName" },
      { text: "Packages Sold", value: "numberOfWinners" },
      { text: "Invoice Total", value: "eventReportTotal" },
      {
        text: "Est. Commission",
        value: "estimatedCommission",
        sortable: false,
      },
      {
        text: "Legacy Match",
        value: "matchValue",
        sortable: false,
      },
    ],
  }),

  computed: {
    canEditAuctioneers() {
      const user = this.$store.state.user;
      return can(user, this.$store, "editAuctioneers");
    },

    isNewRecord() {
      return this.$route.query.source === "new";
    },

    collectionName() {
      return this.isNewRecord
        ? "auctioneerRecords"
        : "auctioneers";
    },

    displayAddress() {
      if (this.isNewRecord) {
        return (
          this.auctioneer.mailingAddress ||
          "No mailing address entered"
        );
      }

      return (
        this.auctioneer.address ||
        "No mailing address entered"
      );
    },

    defaultCommissionDisplay() {
      if (
        !this.isNewRecord ||
        this.auctioneer.defaultCommissionRate === undefined ||
        this.auctioneer.defaultCommissionRate === null ||
        this.auctioneer.defaultCommissionRate === ""
      ) {
        return "Not set";
      }

      return `${Number(this.auctioneer.defaultCommissionRate)}%`;
    },

    hasDefaultCommission() {
      return (
        this.isNewRecord &&
        this.auctioneer.defaultCommissionRate !== undefined &&
        this.auctioneer.defaultCommissionRate !== null &&
        this.auctioneer.defaultCommissionRate !== ""
      );
    },

    associatedTitle() {
      if (!this.isNewRecord) {
        return "Associated Auctioneers / Companies";
      }

      if (this.auctioneer.type === "company") {
        return "Auctioneers in this Company";
      }

      return "Auctioneer Companies";
    },

    combinedSalesYtd() {
      if (this.isNewRecord) return 0;

      const currentSales =
        Number(this.auctioneer.salesYtd) || 0;

      const linkedSales = this.linkedAuctioneers.reduce(
        (total, item) => {
          return total + (Number(item.salesYtd) || 0);
        },
        0
      );

      return currentSales + linkedSales;
    },

    matchingNames() {
      if (!this.auctioneer.name) {
        return [];
      }

      const names = [
        this.normalizeName(this.auctioneer.name),
      ];

      if (
        this.isNewRecord &&
        this.auctioneer.type === "company"
      ) {
        this.linkedAuctioneers.forEach((item) => {
          const normalized = this.normalizeName(item.name);

          if (normalized) {
            names.push(normalized);
          }
        });
      }

      return [...new Set(names.filter(Boolean))];
    },

    matchedEventReports() {
      if (!this.matchingNames.length) {
        return [];
      }

      const currentName = this.normalizeName(
        this.auctioneer.name
      );

      const matches = this.allEventReports.filter((report) => {
        if (!report || report.archived === true) {
          return false;
        }

        const auctioneerName = this.normalizeName(
          report.auctioneerName
        );

        const auctioneerCompany = this.normalizeName(
          report.auctioneerCompany
        );

        if (!this.isNewRecord) {
          return (
            auctioneerName === currentName ||
            auctioneerCompany === currentName
          );
        }

        if (this.auctioneer.type === "company") {
          return (
            this.matchingNames.includes(auctioneerName) ||
            this.matchingNames.includes(auctioneerCompany)
          );
        }

        return (
          auctioneerName === currentName ||
          auctioneerCompany === currentName
        );
      });

      const uniqueReports = new Map();

      matches.forEach((report) => {
        const key =
          report.id ||
          String(report.eventReportNumber || "");

        if (!key) return;

        if (!uniqueReports.has(key)) {
          uniqueReports.set(key, report);
        }
      });

      return Array.from(uniqueReports.values()).sort((a, b) => {
        const aDate = a.eventDate || "";
        const bDate = b.eventDate || "";

        return bDate.localeCompare(aDate);
      });
    },

    reportYearOptions() {
      const years = this.matchedEventReports
        .map((report) => this.getEventDateParts(report.eventDate).year)
        .filter(Boolean);

      return [...new Set(years)].sort((a, b) => b - a);
    },

    hasActiveReportFilters() {
      return Boolean(
        this.reportYear ||
        this.reportQuarter ||
        this.reportMonth
      );
    },

    filteredEventReports() {
      return this.matchedEventReports.filter((report) => {
        const parts = this.getEventDateParts(report.eventDate);

        if (!parts.year || !parts.month) {
          return !this.hasActiveReportFilters;
        }

        if (
          this.reportYear &&
          parts.year !== Number(this.reportYear)
        ) {
          return false;
        }

        if (
          this.reportQuarter &&
          parts.quarter !== Number(this.reportQuarter)
        ) {
          return false;
        }

        if (
          this.reportMonth &&
          parts.month !== Number(this.reportMonth)
        ) {
          return false;
        }

        return true;
      });
    },

    reportTotalSales() {
      return this.filteredEventReports.reduce(
        (total, report) => {
          return total + (Number(report.eventReportTotal) || 0);
        },
        0
      );
    },

    reportTotalPackages() {
      return this.filteredEventReports.reduce(
        (total, report) => {
          return total + this.getPackageCount(report);
        },
        0
      );
    },

    reportEstimatedCommission() {
      return this.filteredEventReports.reduce(
        (total, report) => {
          return total + this.getEstimatedCommission(report);
        },
        0
      );
    },
  },

  watch: {
    "$route.params.id": {
      immediate: true,
      async handler() {
        await this.getRecord();
      },
    },

    "$route.query.source": {
      async handler() {
        await this.getRecord();
      },
    },
  },

  methods: {
    async getRecord() {
      const id = this.$route.params.id;
      if (!id) return;

      this.loading = true;
      this.auctioneer = {};
      this.allAuctioneers = [];
      this.linkedAuctioneers = [];
      this.allEventReports = [];

      try {
        const requests = [
          portalsApp
            .firestore()
            .collection(this.collectionName)
            .doc(id)
            .get(),

          portalsApp
            .firestore()
            .collection(this.collectionName)
            .get(),
        ];

        this.eventReportsLoading = true;

        requests.push(
          portalsApp
            .firestore()
            .collection("eventReports")
            .get()
        );

        const results = await Promise.all(requests);

        const recordSnapshot = results[0];
        const allSnapshot = results[1];
        const eventReportsSnapshot = results[2] || null;

        if (!recordSnapshot.exists) {
          this.$toast.error("Auctioneer record not found", {
            timeout: 3000,
          });

          this.$router.push("/auctioneerrecords");
          return;
        }

        const records = allSnapshot.docs.map((doc) => {
          const data = doc.data();

          if (this.isNewRecord) {
            return {
              ...data,
              id: data.id || doc.id,
              companyIds: Array.isArray(data.companyIds)
                ? data.companyIds
                : [],
              active:
                data.active === undefined ||
                data.active === null
                  ? true
                  : data.active,
              defaultCommissionRate:
                data.defaultCommissionRate === undefined ||
                data.defaultCommissionRate === null ||
                data.defaultCommissionRate === ""
                  ? null
                  : Number(data.defaultCommissionRate),
            };
          }

          return {
            ...data,
            id: data.id || doc.id,
            auctioneers: Array.isArray(data.auctioneers)
              ? data.auctioneers
              : [],
            salesYtd: Number(data.salesYtd) || 0,
          };
        });

        this.allAuctioneers = records;

        const data = recordSnapshot.data();

        if (this.isNewRecord) {
          this.auctioneer = {
            ...data,
            id: data.id || recordSnapshot.id,
            companyIds: Array.isArray(data.companyIds)
              ? data.companyIds
              : [],
            active:
              data.active === undefined ||
              data.active === null
                ? true
                : data.active,
            defaultCommissionRate:
              data.defaultCommissionRate === undefined ||
              data.defaultCommissionRate === null ||
              data.defaultCommissionRate === ""
                ? null
                : Number(data.defaultCommissionRate),
          };
        } else {
          this.auctioneer = {
            ...data,
            id: data.id || recordSnapshot.id,
            auctioneers: Array.isArray(data.auctioneers)
              ? data.auctioneers
              : [],
            salesYtd: Number(data.salesYtd) || 0,
          };
        }

        this.setLinkedAuctioneers();

        if (eventReportsSnapshot) {
          this.allEventReports =
            eventReportsSnapshot.docs.map((doc) => {
              const report = doc.data();

              return {
                ...report,
                id: report.id || doc.id,
                eventReportTotal:
                  Number(report.eventReportTotal) || 0,
                numberOfWinners:
                  Number(report.numberOfWinners) || 0,
              };
            });
        }
      } catch (error) {
        console.error(
          "Error loading auctioneer record:",
          error
        );

        this.$toast.error(
          "Unable to load auctioneer record",
          {
            timeout: 3000,
          }
        );
      } finally {
        this.loading = false;
        this.eventReportsLoading = false;
      }
    },

    setLinkedAuctioneers() {
      if (!this.isNewRecord) {
        this.linkedAuctioneers =
          this.auctioneer.auctioneers
            .map((auctioneerId) => {
              return this.allAuctioneers.find(
                (item) => item.id === auctioneerId
              );
            })
            .filter(Boolean)
            .sort((a, b) =>
              (a.name || "").localeCompare(b.name || "")
            );

        return;
      }

      // New company record:
      // Find all auctioneers whose companyIds contain this company ID.
      if (this.auctioneer.type === "company") {
        this.linkedAuctioneers =
          this.allAuctioneers
            .filter((item) => {
              return (
                item.type === "auctioneer" &&
                Array.isArray(item.companyIds) &&
                item.companyIds.includes(this.auctioneer.id)
              );
            })
            .sort((a, b) =>
              (a.name || "").localeCompare(b.name || "")
            );

        return;
      }

      // New individual auctioneer:
      // Resolve the companyIds saved on this person.
      this.linkedAuctioneers =
        (this.auctioneer.companyIds || [])
          .map((companyId) => {
            return this.allAuctioneers.find(
              (item) =>
                item.id === companyId &&
                item.type === "company"
            );
          })
          .filter(Boolean)
          .sort((a, b) =>
            (a.name || "").localeCompare(b.name || "")
          );
    },

    normalizeName(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
    },

    getEventDateParts(value) {
      if (!value) {
        return {
          year: null,
          month: null,
          quarter: null,
        };
      }

      const parts = String(value).split("-");

      if (parts.length !== 3) {
        return {
          year: null,
          month: null,
          quarter: null,
        };
      }

      const year = Number(parts[0]);
      const month = Number(parts[1]);

      if (!year || !month || month < 1 || month > 12) {
        return {
          year: null,
          month: null,
          quarter: null,
        };
      }

      return {
        year,
        month,
        quarter: Math.ceil(month / 3),
      };
    },

    clearReportFilters() {
      this.reportYear = null;
      this.reportQuarter = null;
      this.reportMonth = null;
    },

    formatDate(value) {
      if (!value) return "�";

      const parts = String(value).split("-");

      if (parts.length !== 3) {
        return value;
      }

      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    },

    getPackageCount(report) {
      if (!report) return 0;

      const storedCount = Number(report.numberOfWinners);

      if (!Number.isNaN(storedCount) && storedCount > 0) {
        return storedCount;
      }

      if (Array.isArray(report.winners)) {
        return report.winners.length;
      }

      if (Array.isArray(report.winnerPackageIDs)) {
        return report.winnerPackageIDs.length;
      }

      return 0;
    },

    getEstimatedCommission(report) {
      if (!this.hasDefaultCommission) {
        return 0;
      }

      const rate =
        Number(this.auctioneer.defaultCommissionRate) || 0;

      const total =
        Number(report && report.eventReportTotal) || 0;

      return +((total * rate) / 100).toFixed(2);
    },

    openAuctioneer(item) {
      if (!item || !item.id) return;

      this.$router.push({
        path: `/auctioneerrecords/${item.id}`,
        query: {
          source: this.isNewRecord
            ? "new"
            : "legacy",
        },
      });
    },

    async updateRecord(payload) {
      if (
        !this.isNewRecord ||
        !this.canEditAuctioneers ||
        !this.auctioneer.id
      ) {
        return;
      }

      this.saving = true;

      try {
        const record = {
          ...payload,
          updatedAt: new Date(),
        };

        delete record.id;

        await portalsApp
          .firestore()
          .collection("auctioneerRecords")
          .doc(this.auctioneer.id)
          .update(record);

        this.$toast.success(
          "Auctioneer record updated",
          {
            timeout: 2000,
          }
        );

        this.editDialog = false;
        await this.getRecord();
      } catch (error) {
        console.error(
          "Error updating auctioneer record:",
          error
        );

        this.$toast.error(
          "Unable to update auctioneer record",
          {
            timeout: 3000,
          }
        );
      } finally {
        this.saving = false;
      }
    },

    async deleteRecord() {
      if (
        !this.isNewRecord ||
        !this.canEditAuctioneers ||
        !this.auctioneer.id
      ) {
        return;
      }

      this.deleting = true;

      try {
        const id = this.auctioneer.id;
        const name = this.auctioneer.name;

        // If this is a company, remove its ID from any auctioneers
        // currently linked to it before deleting the company record.
        if (this.auctioneer.type === "company") {
          const linkedSnapshot = await portalsApp
            .firestore()
            .collection("auctioneerRecords")
            .where("companyIds", "array-contains", id)
            .get();

          for (const doc of linkedSnapshot.docs) {
            const data = doc.data();

            const updatedCompanyIds = Array.isArray(data.companyIds)
              ? data.companyIds.filter(
                  (companyId) => companyId !== id
                )
              : [];

            await portalsApp
              .firestore()
              .collection("auctioneerRecords")
              .doc(doc.id)
              .update({
                companyIds: updatedCompanyIds,
                updatedAt: new Date(),
              });
          }
        }

        await portalsApp
          .firestore()
          .collection("auctioneerRecords")
          .doc(id)
          .delete();

        this.$toast.success(
          `${name || "Auctioneer record"} deleted`,
          {
            timeout: 2000,
          }
        );

        this.deleteDialog = false;

        this.$router.push("/auctioneerrecords");
      } catch (error) {
        console.error(
          "Error deleting auctioneer record:",
          error
        );

        this.$toast.error(
          "Unable to delete auctioneer record",
          {
            timeout: 3000,
          }
        );
      } finally {
        this.deleting = false;
      }
    },

    getInitials(name) {
      if (!name) return "?";

      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((word) =>
          word.charAt(0).toUpperCase()
        )
        .join("");
    },
  },
};
</script>

<style scoped>
.auctioneer-info {
  min-height: 120px;
  white-space: pre-wrap;
}

::v-deep .auctioneer-info p:last-child {
  margin-bottom: 0;
}

::v-deep .v-list-item {
  cursor: pointer;
}
</style>